import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import CodeNameValue from "../../components/CodeNameValue/CodeNameValue";
import { TwoPagePdfViewer } from "../../components/TwoPagePdfViewer/TwoPagePdfViewer";
import ServiceUnavailableModal from "../../common/ServiceUnavailableModal/ServiceUnavailableModal";
import { useTheme } from "../../store/ThemeContext";
import { isCodeNameObject } from "../../utils/fieldValueDisplay";
import isPdfFile from "../../utils/isPdfFile";
import {
  buildVcVerificationDisplayData,
  formatVerificationCreatedAt,
  getDisplayCredentialId,
  isApostilleDisplayLabel,
  isCredentialSubjectEmpty,
} from "../../utils/vcVerificationDisplay";
import PublicPortalHeader from "../../components/PublicVerificationPortal/PublicPortalHeader";
import PublicDocumentPreviewCard from "../../components/PublicVerificationPortal/PublicDocumentPreviewCard";
import { PublicPortalDocumentPreviewSkeleton } from "../../components/PublicVerificationPortal/PublicPortalSkeleton";
import { isPublicDualDocumentPreviewEnabled } from "../../configs/featureFlags";
import {
  PUBLIC_VERIFICATION_RESULT_KEY,
  clearPublicVerificationSession,
  readUploadPreview,
} from "../../utils/publicVerificationUploadStorage";
import {
  getPublicVerificationStatusLabel,
  getPublicVerificationStatusMessage,
  getPublicVerificationStatusVisualKey,
} from "../../utils/publicVerificationStatus";

const readStoredResults = () => {
  try {
    const stored = JSON.parse(
      sessionStorage.getItem(PUBLIC_VERIFICATION_RESULT_KEY),
    );
    if (Array.isArray(stored)) return stored;
    if (stored) return [stored];
    return [];
  } catch {
    return [];
  }
};

const resolveInitialResults = (locationState) => {
  if (Array.isArray(locationState?.results) && locationState.results.length) {
    return locationState.results;
  }
  if (locationState?.result) {
    return [locationState.result];
  }
  return readStoredResults();
};

const getPresentationFileUrl = (entry) => {
  const value = entry?.presentation_file;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const PublicVerificationResultPage = () => {
  const { t, i18n } = useTranslation(["publicPortal", "verify"]);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const portalAssets = theme?.portalAssets || null;
  const isLoadingPortalAssets = theme?.isLoadingPortalAssets ?? false;
  const dualPreviewEnabled = isPublicDualDocumentPreviewEnabled();

  const [results] = useState(() => resolveInitialResults(location.state));
  const [activeIndex, setActiveIndex] = useState(0);
  const [uploadPreview] = useState(
    () => location.state?.upload || readUploadPreview(),
  );
  const [uploadedImageLoading, setUploadedImageLoading] = useState(() => {
    const initialResult = resolveInitialResults(location.state)[0] || null;
    const presentation = getPresentationFileUrl(initialResult);
    const upload = location.state?.upload || readUploadPreview();
    const url = presentation || upload?.document;
    if (!url) return false;
    return !isPdfFile(url, presentation ? null : upload?.fileType);
  });
  const result = results[activeIndex] || null;
  const presentationFileUrl = getPresentationFileUrl(result);
  const uploadedDocumentUrl = uploadPreview?.document || null;
  const hasPresentationPreview = Boolean(presentationFileUrl);
  const hasUploadedPreview = Boolean(uploadedDocumentUrl);
  const previewCount = dualPreviewEnabled
    ? Number(hasPresentationPreview) + Number(hasUploadedPreview)
    : Number(hasPresentationPreview || hasUploadedPreview);
  const hasDocumentPreview = previewCount > 0;
  const documentUrl = dualPreviewEnabled
    ? null
    : presentationFileUrl || uploadedDocumentUrl || null;
  const documentFileType = dualPreviewEnabled
    ? null
    : presentationFileUrl
      ? null
      : uploadPreview?.fileType || null;
  const isUsingUploadFallback =
    !dualPreviewEnabled && !presentationFileUrl && hasUploadedPreview;
  const isDocumentPdf = isPdfFile(documentUrl, documentFileType);
  const hasMissingCredentialSubject = isCredentialSubjectEmpty(
    result?.credential_subject,
  );
  const [showServiceUnavailableModal, setShowServiceUnavailableModal] =
    useState(() => Boolean(result) && hasMissingCredentialSubject);

  useEffect(() => {
    if (results.length > 0) {
      const usable = results.filter(
        (entry) => !isCredentialSubjectEmpty(entry?.credential_subject),
      );
      if (usable.length > 0) {
        sessionStorage.setItem(
          PUBLIC_VERIFICATION_RESULT_KEY,
          JSON.stringify(usable),
        );
      }
    }
  }, [results]);

  useEffect(() => {
    if (results.length > 1) {
      setShowServiceUnavailableModal(false);
      return;
    }
    setShowServiceUnavailableModal(
      Boolean(result) && isCredentialSubjectEmpty(result?.credential_subject),
    );
  }, [result, results.length]);

  useEffect(() => {
    if (dualPreviewEnabled) return;
    if (!documentUrl || isDocumentPdf) {
      setUploadedImageLoading(false);
      return;
    }
    setUploadedImageLoading(true);
  }, [documentUrl, isDocumentPdf, dualPreviewEnabled]);

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem("selectedLanguage", language);
    document.documentElement.lang = language;
  };

  const handleBack = () => {
    clearPublicVerificationSession();
    navigate("/");
  };

  const handleServiceUnavailableClose = () => {
    setShowServiceUnavailableModal(false);
    if (results.length <= 1) {
      handleBack();
    }
  };

  const goToPreviousResult = () => {
    setActiveIndex((current) => Math.max(0, current - 1));
  };

  const goToNextResult = () => {
    setActiveIndex((current) => Math.min(results.length - 1, current + 1));
  };

  const formatFieldName = (fieldName) =>
    fieldName
      .replace(/_/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/Id$/i, "ID")
      .replace(/Number$/i, "No.");

  const renderFieldValue = (value, depth = 0) => {
    if (value === null || value === undefined) {
      return t("verify:not_available");
    }

    if (isCodeNameObject(value)) {
      return <CodeNameValue value={value} />;
    }

    if (Array.isArray(value)) {
      return (
        <div className="mt-2 space-y-2">
          {value.map((item, index) => (
            <div key={index} className="border-l-2 border-gray-300 pl-3">
              {isCodeNameObject(item) ? (
                renderFieldValue(item, depth + 1)
              ) : typeof item === "object" && item !== null ? (
                <div className="space-y-2">
                  {Object.entries(item).map(([key, val]) => (
                    <div
                      key={key}
                      className={
                        isCodeNameObject(val)
                          ? "flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
                          : "flex justify-between gap-2"
                      }
                    >
                      <span className="shrink-0 text-sm font-medium text-gray-600">
                        {formatFieldName(key)}:
                      </span>
                      <span className="min-w-0 flex-1 text-right text-sm text-gray-900">
                        {renderFieldValue(val, depth + 1)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-sm text-gray-900">{String(item)}</span>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (typeof value === "object") {
      return (
        <div className={`${depth > 0 ? "mt-2" : ""} space-y-2`}>
          {Object.entries(value).map(([key, val]) => (
            <div
              key={key}
              className={
                isCodeNameObject(val)
                  ? "flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between"
                  : "flex justify-between gap-2"
              }
            >
              <span className="shrink-0 text-sm font-medium text-gray-600">
                {formatFieldName(key)}:
              </span>
              <span className="min-w-0 flex-1 text-right text-sm text-gray-900">
                {renderFieldValue(val, depth + 1)}
              </span>
            </div>
          ))}
        </div>
      );
    }

    return String(value);
  };

  const statusConfig = {
    approved: {
      icon: Check,
      color: "#4caf50",
      bgColor: "#3CB90F",
    },
    revoked: {
      icon: X,
      color: "#dc2626",
      bgColor: "#dc2626",
    },
    expired: {
      icon: AlertTriangle,
      color: "#dc2626",
      bgColor: "#dc2626",
    },
    rejected: {
      icon: X,
      color: "#dc2626",
      bgColor: "#dc2626",
    },
  };

  const brand = t("publicPortal:brand", { returnObjects: true });
  const credentialId = result ? getDisplayCredentialId(result) : null;
  const formattedCreatedAt = result
    ? formatVerificationCreatedAt(result.issued_at || result.created_at)
    : null;
  const verificationData = result
    ? buildVcVerificationDisplayData(result, t("verify:not_available"))
    : {};
  const isApostille = Object.keys(verificationData).some((key) =>
    isApostilleDisplayLabel(key),
  );
  const displayStatus = result
    ? getPublicVerificationStatusLabel(result, t)
    : null;
  const statusMessage = result
    ? getPublicVerificationStatusMessage(result, t)
    : null;
  const showPager = results.length > 1;

  const resolveStatusConfig = () => {
    const visualKey = getPublicVerificationStatusVisualKey(result);
    return statusConfig[visualKey] || statusConfig.rejected;
  };

  const renderLegacyDocumentPreview = () =>
    hasDocumentPreview ? (
      <div className="flex min-h-0 min-w-0 w-full flex-col overflow-hidden rounded-xl border border-black p-4 shadow-sm">
        <div className="mb-2 flex shrink-0 items-center space-x-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
            <FileText className="h-5 w-5 text-orange-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isUsingUploadFallback
                ? t("verify:uploaded_document")
                : t("publicPortal:verification.document")}
            </h3>
          </div>
        </div>
        <div className="mt-3 min-h-0 flex-1 overflow-hidden rounded-lg bg-gray-200">
          <div className="relative h-full w-full overflow-auto rounded-lg">
            {uploadedImageLoading && <PublicPortalDocumentPreviewSkeleton />}
            {isDocumentPdf ? (
              <TwoPagePdfViewer
                pdfUrl={documentUrl}
                showZoom={false}
                heightClassName="h-full max-h-full"
              />
            ) : (
              <img
                src={documentUrl}
                alt={
                  isUsingUploadFallback
                    ? t("publicPortal:verification.previewAlt")
                    : t("publicPortal:verification.documentAlt")
                }
                className={`mx-auto max-h-full w-full object-contain ${
                  uploadedImageLoading ? "hidden" : "block"
                }`}
                onLoad={() => setUploadedImageLoading(false)}
                onError={() => setUploadedImageLoading(false)}
              />
            )}
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div
      aria-busy={isLoadingPortalAssets}
      className="min-h-screen min-w-0 overflow-x-hidden bg-cfSecondary text-cfHeadingText"
    >
      <PublicPortalHeader
        assets={portalAssets}
        brand={brand}
        isLoading={isLoadingPortalAssets}
        onLanguageChange={handleLanguageChange}
        showLanguageSwitch={
          Object.keys(portalAssets?.languages || {}).length > 1
        }
      />

      <main className="w-full min-w-0 px-4 py-6 sm:px-8 sm:py-8 lg:px-12">
        <button
          type="button"
          className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-cfPrimary"
          onClick={handleBack}
        >
          <ArrowLeft aria-hidden="true" size={18} />
          {t("publicPortal:verification.backToVerify")}
        </button>

        {!result || hasMissingCredentialSubject ? (
          <div className="rounded-xl border border-black p-8 text-center shadow-sm">
            <p className="m-0 text-gray-500">
              {t("publicPortal:verification.resultUnavailable")}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex w-full flex-col justify-between gap-4 rounded-xl border border-black p-4 shadow-sm lg:flex-row">
              <div className="flex min-w-0 flex-1 flex-col gap-4">
                <div className="flex items-start gap-2">
                  {(() => {
                    const config = resolveStatusConfig();
                    const IconComponent = config.icon;
                    return (
                      <div
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${config.bgColor}20` }}
                      >
                        <IconComponent
                          strokeWidth={2.5}
                          className="h-5 w-5"
                          style={{ color: config.color }}
                        />
                      </div>
                    );
                  })()}
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="text-lg font-semibold text-gray-900">
                      {t("publicPortal:verification.status")}:{" "}
                      <span>{displayStatus}</span>
                    </div>
                    <div className="text-sm text-gray-900">{statusMessage}</div>
                    {formattedCreatedAt && (
                      <div className="mt-1 text-sm text-gray-600">
                        <span className="font-medium text-gray-700">
                          {t("verify:created_at")}:
                        </span>{" "}
                        {formattedCreatedAt}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {credentialId && (
                <div className="flex shrink-0 flex-col items-stretch gap-3 sm:flex-row sm:items-end lg:flex-col lg:items-end">
                  <div className="flex min-w-[12rem] flex-col items-start gap-1 rounded-lg border border-blue-500/20 bg-blue-500/5 px-4 py-3 sm:items-end">
                    <span className="text-xs font-semibold uppercase tracking-wide text-blue-600">
                      {t("verify:credential_id")}
                    </span>
                    <span className="break-all text-left text-base font-semibold text-gray-900 sm:text-right sm:text-lg">
                      {credentialId}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div
              className={`mt-3 grid w-full min-w-0 items-stretch gap-3 [&>*]:min-w-0 ${
                dualPreviewEnabled && previewCount >= 2
                  ? "md:grid-cols-2 lg:grid-cols-3 [&>*]:h-[65vh] md:[&>*]:h-[70vh]"
                  : hasDocumentPreview
                    ? "md:grid-cols-2 [&>*]:h-[65vh] md:[&>*]:h-[70vh]"
                    : ""
              }`}
            >
              <div className="flex min-h-0 min-w-0 w-full max-w-full flex-col overflow-hidden rounded-xl border border-black p-4 shadow-sm">
                <div className="mb-4 flex shrink-0 items-center space-x-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
                    <FileText className="h-5 w-5 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {t("publicPortal:verification.verificationDetails")}
                    </h3>
                  </div>
                </div>

                {Object.keys(verificationData).length > 0 ? (
                  <div className="relative min-h-0 flex-1 overflow-hidden">
                    <div className="h-full space-y-4 overflow-y-auto pb-2 [mask-image:linear-gradient(to_bottom,black_85%,transparent_100%)]">
                      {Object.entries(verificationData).map(
                        ([fieldName, fieldValue]) => {
                          if (fieldName.startsWith("__section__")) {
                            const i18nKey = fieldName.replace(
                              "__section__",
                              "",
                            );
                            return (
                              <div key={fieldName} className="pb-2 pt-1">
                                <h4 className="text-base font-bold italic text-gray-800">
                                  {t(`verify:${i18nKey}`)}
                                </h4>
                              </div>
                            );
                          }

                          if (fieldName.startsWith("__pair__")) {
                            const { left, right } = fieldValue;
                            const renderPreview = (url) => {
                              if (!url) {
                                return (
                                  <span className="text-sm text-gray-400">
                                    {t("verify:not_available")}
                                  </span>
                                );
                              }
                              if (isPdfFile(url)) {
                                return (
                                  <TwoPagePdfViewer
                                    pdfUrl={url}
                                    showZoom={false}
                                    heightClassName="max-h-[200px]"
                                  />
                                );
                              }
                              return (
                                <img
                                  src={url}
                                  alt="document preview"
                                  className="max-h-[200px] w-full rounded border border-gray-200 object-contain"
                                />
                              );
                            };
                            return (
                              <div
                                key={fieldName}
                                className="border-b border-gray-200 pb-4"
                              >
                                <div className="flex min-w-0 flex-col gap-4 sm:flex-row">
                                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                                    <div className="text-md font-medium text-gray-600">
                                      {t(`verify:${left.i18nKey}`)}:
                                    </div>
                                    {renderPreview(left.value)}
                                  </div>
                                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                                    <div className="text-md font-medium text-gray-600">
                                      {t(`verify:${right.i18nKey}`)}:
                                    </div>
                                    {renderPreview(right.value)}
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          const displayLabel = isApostilleDisplayLabel(
                            fieldName,
                          )
                            ? t(`verify:${fieldName}`)
                            : formatFieldName(fieldName);
                          const useStackedLayout =
                            isApostilleDisplayLabel(fieldName);

                          return (
                            <div
                              key={fieldName}
                              className="border-b border-gray-200 pb-4"
                            >
                              <div
                                className={
                                  useStackedLayout
                                    ? "flex flex-col gap-2"
                                    : "flex items-start justify-between"
                                }
                              >
                                <div
                                  className={`text-md break-words font-medium text-gray-600 ${
                                    useStackedLayout
                                      ? "leading-snug"
                                      : "mr-4 min-w-0 sm:flex-shrink-0"
                                  }`}
                                >
                                  {displayLabel}:
                                </div>
                                <div
                                  className={`text-md min-w-0 flex-1 break-words font-normal text-gray-900 ${
                                    useStackedLayout ? "" : "sm:text-right"
                                  }`}
                                >
                                  {renderFieldValue(fieldValue)}
                                </div>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    {t("verify:no_verification_data")}
                  </div>
                )}
              </div>

              {dualPreviewEnabled ? (
                <>
                  {hasUploadedPreview && (
                    <PublicDocumentPreviewCard
                      title={t("publicPortal:verification.uploadedDocument")}
                      url={uploadedDocumentUrl}
                      fileType={uploadPreview?.fileType || null}
                      alt={t("publicPortal:verification.previewAlt")}
                    />
                  )}

                  {hasPresentationPreview && (
                    <PublicDocumentPreviewCard
                      title={t("publicPortal:verification.retrievedDocument")}
                      url={presentationFileUrl}
                      alt={t("publicPortal:verification.documentAlt")}
                      allowDownload
                      downloadFileName={`retrieved-document-${credentialId || "file"}`}
                    />
                  )}
                </>
              ) : (
                renderLegacyDocumentPreview()
              )}
            </div>

            {isApostille && (
              <div className="mt-3 flex items-start gap-3 rounded-xl bg-amber-50 p-3 sm:mt-4 sm:p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 sm:h-5 sm:w-5" />
                <p className="text-sm leading-relaxed text-amber-900 sm:text-base">
                  <span className="font-semibold">
                    {t("verify:apostille_note_label")}{" "}
                  </span>
                  {t("verify:apostille_note")}
                </p>
              </div>
            )}

            {showPager && (
              <div className="flex min-w-0 items-center justify-between gap-3 overflow-hidden rounded-xl border border-black px-3 py-3 shadow-sm sm:px-4">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg border border-cfCardBorder bg-cfSecondary px-3 py-2 text-sm font-semibold text-cfHeadingText disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={goToPreviousResult}
                  disabled={activeIndex === 0}
                  aria-label={t("publicPortal:verification.previousCredential")}
                >
                  <ChevronLeft aria-hidden="true" size={18} />
                  {t("publicPortal:verification.previousCredential")}
                </button>
                <span className="text-sm font-semibold text-gray-700">
                  {t("publicPortal:verification.resultPageOf", {
                    current: activeIndex + 1,
                    total: results.length,
                  })}
                </span>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg border border-cfCardBorder bg-cfSecondary px-3 py-2 text-sm font-semibold text-cfHeadingText disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={goToNextResult}
                  disabled={activeIndex >= results.length - 1}
                  aria-label={t("publicPortal:verification.nextCredential")}
                >
                  {t("publicPortal:verification.nextCredential")}
                  <ChevronRight aria-hidden="true" size={18} />
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      <ServiceUnavailableModal
        isOpen={showServiceUnavailableModal}
        onClose={handleServiceUnavailableClose}
      />
    </div>
  );
};

export default PublicVerificationResultPage;
